import {useState} from 'react';
import {useDisclosure} from '@mantine/hooks';
import type {
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  MRT_SortingState,
} from 'mantine-react-table';
import React from 'react';
import type {CourseDefinitionSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {
  useBulkSetCourseDefinitionActiveMutation,
  useCloneCourseDefinitionMutation,
  useGetCourseDefinitionListV2Query,
} from 'src/redux/queries/courseDefinition';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useCourseDefinitionColumns} from './hooks/useCourseDefinitionColumns';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface CourseDefinitionProps {
  identifier: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;

  onViewRowClick: (row: CourseDefinitionSimpleResponse) => void;
}

export const CourseDefinitionTable: React.FC<CourseDefinitionProps> = ({
  identifier,
  defaultSorting,
  defaultColumnFilters,
  onViewRowClick,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const {hasPermission: hasCreatePermission} = useHasModulePermission({
    module: ModuleEnum.Training,
    operation: PermissionOperationEnum.Create,
  });
  const {hasPermission: hasUpdatePermission} = useHasModulePermission({
    module: ModuleEnum.Training,
    operation: PermissionOperationEnum.Update,
  });

  const handleAddNew = () => navigate(PathUtility.TrainingCourseCreate());

  const columns = useCourseDefinitionColumns();

  const [cloneCourseDefinition, {isLoading: isCloneLoading}] =
    useCloneCourseDefinitionMutation();
  const [bulkSetCourseDefinitionActive, {isLoading: isBulkActiveLoading}] =
    useBulkSetCourseDefinitionActiveMutation();

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetCourseDefinitionListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const selectedCount = Object.keys(rowSelection ?? {}).length;
  const selectedIds = rows
    .filter(row => rowSelection[String(row.id)])
    .map(row => row.id!);

  const [pendingBulkActive, setPendingBulkActive] = useState<
    boolean | undefined
  >(undefined);
  const [confirmBulkActiveOpened, confirmBulkActiveHandlers] =
    useDisclosure(false);

  const [cloneTargetId, setCloneTargetId] = useState<number | undefined>(
    undefined
  );
  const [confirmCloneOpened, confirmCloneHandlers] = useDisclosure(false);

  const requestBulkActive = (active: boolean) => {
    if (selectedCount === 0) {
      return;
    }
    setPendingBulkActive(active);
    confirmBulkActiveHandlers.open();
  };

  const handleBulkActive = async () => {
    const active = pendingBulkActive;
    confirmBulkActiveHandlers.close();

    if (active === undefined || selectedIds.length === 0) {
      return;
    }

    try {
      const count = await bulkSetCourseDefinitionActive({
        ids: selectedIds,
        active,
      }).unwrap();
      setAlert(
        `Successfully ${active ? 'activated' : 'deactivated'} ${count} course${
          count === 1 ? '' : 's'
        }.`,
        'success'
      );
      setRowSelection({});
      void refetch();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setPendingBulkActive(undefined);
  };

  const requestClone = (id: number) => {
    setCloneTargetId(id);
    confirmCloneHandlers.open();
  };

  const handleClone = async () => {
    const id = cloneTargetId;
    confirmCloneHandlers.close();
    setCloneTargetId(undefined);

    if (!id) {
      return;
    }

    try {
      const cloned = await cloneCourseDefinition(id).unwrap();
      setAlert('Successfully cloned course.', 'success');
      navigate(PathUtility.TrainingCourseView(String(cloned.id), 'build'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const {table} = useTable({
    ...tableState,
    rowSelection,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowActions: true,
      enableRowSelection: hasUpdatePermission,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      onRowSelectionChange: setRowSelection,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View onClick={() => onViewRowClick(row.original)} />
          {hasCreatePermission && (
            <TableRowActions.Menu>
              <TableRowActions.MenuItem
                onClick={() => requestClone(row.original.id)}>
                Clone
              </TableRowActions.MenuItem>
            </TableRowActions.Menu>
          )}
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {hasCreatePermission && (
              <TableToolbar.Action variant="create" onClick={handleAddNew} />
            )}
            {hasUpdatePermission && (
              <>
                <TableToolbar.Action
                  variant="secondary"
                  onClick={() => requestBulkActive(true)}
                  loading={isBulkActiveLoading && pendingBulkActive === true}
                  disabled={
                    selectedCount === 0 ||
                    (isBulkActiveLoading && pendingBulkActive !== true)
                  }>{`Activate ${selectedCount}`}</TableToolbar.Action>
                <TableToolbar.Action
                  variant="danger"
                  onClick={() => requestBulkActive(false)}
                  loading={isBulkActiveLoading && pendingBulkActive === false}
                  disabled={
                    selectedCount === 0 ||
                    (isBulkActiveLoading && pendingBulkActive !== false)
                  }>{`Deactivate ${selectedCount}`}</TableToolbar.Action>
              </>
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      {table}
      <ConfirmDialog
        opened={confirmBulkActiveOpened}
        onClose={() => {
          confirmBulkActiveHandlers.close();
          setPendingBulkActive(undefined);
        }}
        onConfirm={handleBulkActive}
        confirmationButtonColor={pendingBulkActive ? 'yellow' : 'red'}
        title={`${
          pendingBulkActive ? 'Activate' : 'Deactivate'
        } ${selectedCount} course${selectedCount === 1 ? '' : 's'}?`}
        body={
          pendingBulkActive
            ? `This will activate ${selectedCount} course${
                selectedCount === 1 ? '' : 's'
              }, making ${
                selectedCount === 1 ? 'it' : 'them'
              } assignable again.`
            : `This will deactivate ${selectedCount} course${
                selectedCount === 1 ? '' : 's'
              }. Deactivated courses cannot receive new assignments.`
        }
      />
      <ConfirmDialog
        opened={confirmCloneOpened}
        onClose={() => {
          confirmCloneHandlers.close();
          setCloneTargetId(undefined);
        }}
        onConfirm={handleClone}
        confirmationButtonColor="yellow"
        title="Clone this course?"
        body="This creates a new course definition with a copy of this course's content. Assignments are not copied."
        confirmationButtonDisabled={isCloneLoading}
      />
    </>
  );
};
